/**
 * Aba Notificacoes: quais avisos a empresa recebe por e-mail e para onde.
 *
 * O e-mail de destino so e obrigatorio se algum aviso estiver ligado: exigir
 * e-mail com tudo desligado seria pedir um dado que nao vai ser usado. Cada
 * interruptor tem a descricao do QUE chega e QUANDO, porque "resumo diario"
 * sozinho nao diz se e as 8h ou a meia-noite.
 *
 * Todo aviso parte de um fato ja ocorrido (abandono, vencimento, estorno,
 * desconexao, evento recusado). Nao existe aviso de "vai vencer": a empresa
 * nao e lembrada de cobrar ninguem antes do fato, porque nao ha comissao
 * sobre quem nao saiu. O texto do estorno segue a POLITICA da empresa
 * (ADR-001, E2): pela padrao, chargeback desfaz a comissao e reembolso nao.
 */

import { useState } from 'react';
import { Field, Footnote, Input, SectionCard, Toggle } from '../../../components/ui';
import type { NotificationSettings, RefundPolicy } from '../types';
import { useRascunho } from '../useRascunho';
import { REFUND_POLICY_INFO, emailValido } from '../vocabulario';
import { BarraSalvar, type ErroDeCampo } from './BarraSalvar';

const ID_EMAIL = 'cfg-avisos-email';

type Aviso = Exclude<keyof NotificationSettings, 'recipientEmail'>;

interface DefinicaoDeAviso {
  readonly key: Aviso;
  readonly label: string;
  readonly description: (politica: RefundPolicy) => string;
}

const AVISOS: readonly DefinicaoDeAviso[] = [
  {
    key: 'dailySummary',
    label: 'Resumo diário',
    description: () => 'Todo dia às 8h: quantos abandonaram ontem, quantos voltaram e o valor recuperado.',
  },
  {
    key: 'webhookRejected',
    label: 'Evento de webhook recusado',
    description: () => 'Assim que uma plataforma enviar um evento que o motor recusou (assinatura inválida, telefone ausente). É a falha silenciosa que mais atrasa uma implantação.',
  },
  {
    key: 'whatsappDisconnected',
    label: 'WhatsApp desconectado',
    description: () => 'Assim que a instância da Evolution perder a sessão. Sem ela, o 2º tier não sai.',
  },
  {
    key: 'chargebackAlert',
    label: 'Reembolso ou chargeback',
    description: (politica) => `Quando um pagamento recuperado é devolvido. Política desta empresa: ${REFUND_POLICY_INFO[politica].label.toLowerCase()} (ADR-001).`,
  },
  {
    key: 'weeklyReport',
    label: 'Relatório semanal',
    description: () => 'Segunda-feira: retorno da semana por funil e por canal, com o comparativo da semana anterior.',
  },
];

export function AbaNotificacoes({ base, refundPolicy }: { readonly base: NotificationSettings; readonly refundPolicy: RefundPolicy }): JSX.Element {
  const rascunho = useRascunho(base);
  const [tentouSalvar, setTentouSalvar] = useState(false);

  const { draft } = rascunho;
  const algumLigado = AVISOS.some((a) => draft[a.key]);
  const erroEmail = algumLigado && !emailValido(draft.recipientEmail) ? 'Informe um e-mail válido para receber os avisos.' : null;
  const erros: readonly ErroDeCampo[] = erroEmail === null ? [] : [{ id: ID_EMAIL, campo: 'E-mail de destino', mensagem: erroEmail }];

  const salvar = (): void => {
    setTentouSalvar(true);
    if (erroEmail === null) {
      rascunho.salvar();
      setTentouSalvar(false);
    }
  };

  const descartar = (): void => {
    setTentouSalvar(false);
    rascunho.descartar();
  };

  return (
    <SectionCard id="notificacoes" title="Avisos por e-mail" icon="alerta" description="O que a empresa recebe do Converte Fácil sem precisar abrir o painel. Todo aviso parte de algo que já aconteceu.">
      <ul className="divide-y divide-line rounded-xl border border-line bg-elevated">
        {AVISOS.map((aviso) => (
          <li key={aviso.key} className="px-4 py-3">
            <Toggle checked={draft[aviso.key]} onChange={(v) => rascunho.patch({ [aviso.key]: v })} label={aviso.label} description={aviso.description(refundPolicy)} />
          </li>
        ))}
      </ul>

      <div className="mt-5 max-w-md">
        <Field
          label="E-mail de destino"
          required={algumLigado}
          htmlFor={ID_EMAIL}
          error={tentouSalvar ? erroEmail : null}
          hint={algumLigado ? 'Todos os avisos acima vão para este endereço.' : 'Nenhum aviso ligado: o endereço fica guardado para quando ligar.'}
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="email"
              inputMode="email"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={draft.recipientEmail}
              onChange={(event) => rascunho.patch({ recipientEmail: event.target.value })}
              autoComplete="email"
            />
          )}
        </Field>
      </div>

      <div className="mt-4">
        <Footnote>Não existe aviso de “plano vai vencer” nem de “carrinho parado há 10 minutos”: a régua e os avisos só falam de quem já saiu.</Footnote>
      </div>

      <BarraSalvar
        className="mt-6"
        dirty={rascunho.dirty}
        saved={rascunho.saved}
        onSave={salvar}
        onDiscard={descartar}
        bloqueado={tentouSalvar && erroEmail !== null}
        motivoBloqueio="Informe o e-mail de destino."
        erros={erros}
      />
    </SectionCard>
  );
}
